import React, { useState } from "react";
import { TabType } from "../../types";
import { ContextMenu, MenuItem, MenuAnchor } from "../common/Menu";

// Every row starts with a fixed-width slot that holds either a folder's chevron
// or a leaf's icon, so chevrons and icons line up in one column (VS Code-style)
// instead of leaves being indented past the caret. Each level indents by STEP.
const SLOT_WIDTH = 18;
const INDENT_STEP = 16;
// The "⋯" affordance sits at the very end of the hovered row. It's only in the
// layout while it's showing, so the count slides left to make room for it and
// the label ellipsifies if that leaves it short.
const DOTS_WIDTH = 16;

/** A collapsible folder (has children) or a leaf that opens a tab. */
export type TreeNode =
  | {
      kind: "folder";
      id: string;
      label: string;
      count?: number;
      children: TreeNode[];
    }
  | {
      kind: "leaf";
      id: TabType;
      label: string;
      icon?: string;
      count?: number;
      /** Render the label in red to flag a problem (e.g. an unsolvable model). */
      danger?: boolean;
      /** Struck through and dimmed: present but left out of the model (a disabled rule). */
      inactive?: boolean;
    };

interface Props {
  nodes: TreeNode[];
  activePanel: TabType | null;
  onSelect: (id: TabType) => void;
  /** Folder ids that should start collapsed. */
  defaultCollapsed?: string[];
  /**
   * The menu for a node, if it has one. Returning an empty list (or nothing)
   * means the row has no commands: no "⋯" appears and right-clicking it leaves
   * the browser's own context menu alone.
   */
  menuFor?: (node: TreeNode) => MenuItem[] | undefined;
}

/**
 * VS Code-style collapsible tree for the elements panel. Folders ("data",
 * "problem", "variables", "constraints") expand/collapse; clicking a leaf
 * opens/focuses its tab. Renders recursively so folders can nest.
 */
const PanelTree: React.FC<Props> = ({
  nodes,
  activePanel,
  onSelect,
  defaultCollapsed = [],
  menuFor,
}) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(defaultCollapsed.map((id) => [id, true]))
  );
  // The open menu: where it is, what's in it, and which row it belongs to (so
  // that row keeps its "⋯" showing while the menu is up).
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; items: MenuItem[]; nodeId: string } | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const toggle = (id: string) =>
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  const menuItemsFor = (node: TreeNode): MenuItem[] => menuFor?.(node) ?? [];

  /** Right-click opens the node's menu at the pointer. */
  const handleContextMenu = (node: TreeNode, items: MenuItem[]) => (event: React.MouseEvent) => {
    if (items.length === 0) return;
    event.preventDefault();
    setMenu({ anchor: { x: event.clientX, y: event.clientY }, items, nodeId: node.id });
  };

  /** Clicking "⋯" opens the same menu, hung under the button. */
  const handleDotsClick = (node: TreeNode, items: MenuItem[]) => (event: React.MouseEvent) => {
    // Don't let the click through to the row, which would select or collapse it.
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenu({ anchor: { x: rect.left, y: rect.bottom + 2 }, items, nodeId: node.id });
  };

  // Fixed-width leading slot shared by folders (chevron) and leaves (icon), so
  // the two align in one column.
  const slotStyle: React.CSSProperties = {
    width: `${SLOT_WIDTH}px`,
    flexShrink: 0,
    textAlign: "center",
    display: "inline-block",
  };
  // Takes the leftover width and gives it back first: `minWidth: 0` lets the
  // label shrink past its text so it ellipsifies rather than pushing the count
  // and the "⋯" out of the row.
  const labelStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const countStyle: React.CSSProperties = { fontSize: "11px", flexShrink: 0 };

  /**
   * The "⋯" button: the visible hint that a row has commands. Rendered at the
   * end of the row, and only while the row is hovered (or its menu is open) —
   * so it always reads as "the last thing on this row", and the count shifts
   * left out of its way.
   */
  const renderDots = (node: TreeNode, items: MenuItem[]): React.ReactNode => {
    const showing = hoveredId === node.id || menu?.nodeId === node.id;
    if (items.length === 0 || !showing) return null;
    return (
      <button
        type="button"
        title={`Actions for ${node.label}`}
        aria-label={`Actions for ${node.label}`}
        onClick={handleDotsClick(node, items)}
        style={{
          width: `${DOTS_WIDTH}px`,
          flexShrink: 0,
          padding: 0,
          marginLeft: "4px",
          border: "none",
          background: "none",
          color: "#666",
          font: "inherit",
          fontSize: "13px",
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        ⋯
      </button>
    );
  };

  const renderNode = (node: TreeNode, depth: number): React.ReactNode => {
    const paddingLeft = 8 + depth * INDENT_STEP;
    const items = menuItemsFor(node);

    if (node.kind === "folder") {
      const isCollapsed = collapsed[node.id];
      return (
        <div key={node.id}>
          <div
            onClick={() => toggle(node.id)}
            onContextMenu={handleContextMenu(node, items)}
            onMouseEnter={() => setHoveredId(node.id)}
            onMouseLeave={() => setHoveredId((id) => (id === node.id ? null : id))}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              paddingLeft: `${paddingLeft}px`,
              cursor: "pointer",
              fontWeight: 600,
              color: "#555",
              fontSize: depth === 0 ? "11px" : "12px",
              textTransform: depth === 0 ? "uppercase" : "none",
              letterSpacing: depth === 0 ? "0.5px" : "normal",
            }}
          >
            {/* Hollow chevron in the shared slot; rotates when expanded. */}
            <span
              style={{
                ...slotStyle,
                fontSize: "14px",
                color: "#888",
                transform: isCollapsed ? "none" : "rotate(90deg)",
                transition: "transform 0.12s ease",
              }}
            >
              ›
            </span>
            <span style={labelStyle}>{node.label}</span>
            {node.count !== undefined && (
              <span style={{ ...countStyle, color: "#aaa" }}>{node.count}</span>
            )}
            {renderDots(node, items)}
          </div>
          {!isCollapsed &&
            node.children.map((child) => renderNode(child, depth + 1))}
        </div>
      );
    }

    const isActive = node.id === activePanel;
    // Danger (red) wins over the normal/active text colour so the flag stays
    // visible even when the leaf is selected.
    const color = node.danger ? "#d32f2f" : isActive ? "#1565c0" : "#333";
    return (
      <div
        key={node.id}
        onClick={() => onSelect(node.id)}
        onContextMenu={handleContextMenu(node, items)}
        onMouseEnter={() => setHoveredId(node.id)}
        onMouseLeave={() => setHoveredId((id) => (id === node.id ? null : id))}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "2px 8px",
          paddingLeft: `${paddingLeft}px`,
          cursor: "pointer",
          background: isActive ? "#e3f0fc" : "transparent",
          color,
          fontWeight: isActive || node.danger ? 600 : 400,
          opacity: node.inactive ? 0.5 : 1,
        }}
      >
        {/* Icon sits in the same slot a folder's chevron would occupy. */}
        <span style={{ ...slotStyle, fontSize: "11px" }}>{node.icon}</span>
        <span
          style={{
            ...labelStyle,
            textDecoration: node.inactive ? "line-through" : "none",
          }}
        >
          {node.label}
        </span>
        {node.count !== undefined && (
          <span style={{ ...countStyle, color: "#999" }}>{node.count}</span>
        )}
        {renderDots(node, items)}
      </div>
    );
  };

  return (
    <div
      style={{
        width: "190px",
        flexShrink: 0,
        borderRight: "1px solid #e0e0e0",
        background: "#fafafa",
        overflow: "auto",
        fontSize: "13px",
        userSelect: "none",
      }}
    >
      {nodes.map((node) => renderNode(node, 0))}
      {menu && (
        <ContextMenu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} />
      )}
    </div>
  );
};

export default PanelTree;
